// Appointments on the device: occurrences for a window, and the changes (applied locally at once, then synced).

import { useLiveQuery } from "dexie-react-hooks";
import { addDays, planningDate } from "@shared/time";
import {
  buildRrule,
  dayBefore,
  normLocal,
  occurrencesBetween,
  repeatOf,
  withStartDay,
  type Occurrence,
  type Repeat,
} from "@shared/recurrence";
import { uuidv7 } from "@shared/uuid";
import { db, getMeta, type Appointment } from "./db";
import type { Me } from "./session";
import { sync } from "./sync";

export interface AppointmentForm {
  title: string;
  location: string;
  notes: string;
  date: string;
  time: string;
  durationMinutes: number;
  repeat: Repeat;
  remindMinutes: number;
  /** Travel time (golf 9): the notification says when to leave; 0 is none. */
  travelMinutes: number;
  /** Who sees it (B43): only me, busy for the other, the whole appointment for the other, or together (B35). */
  who: "private" | "busy" | "shared" | "together";
}

export function useOccurrences(from: string, to: string): Occurrence[] | undefined {
  return useLiveQuery(async () => {
    const [appointments, exceptions] = await Promise.all([db.appointments.toArray(), db.appointment_exceptions.toArray()]);
    return occurrencesBetween(appointments, exceptions, from, to);
  }, [from, to]);
}

function fields(form: AppointmentForm) {
  const repeat = withStartDay(form.repeat, `${form.date}T${form.time}`);
  return {
    title: form.title.trim(),
    location: form.location.trim() || null,
    notes: form.notes.trim() || null,
    startLocal: `${form.date}T${form.time}`,
    durationMinutes: form.durationMinutes,
    repeat,
    remindMinutes: form.remindMinutes,
    travelMinutes: form.travelMinutes || null,
    visibility: form.who === "together" ? ("private" as const) : form.who,
    together: form.who === "together",
  };
}

/** Exceptions follow a new time of day of their series (same as the server). */
async function moveExceptions(fromAppointment: string, toAppointment: string, from: string | null, newTime: string) {
  const list = await db.appointment_exceptions
    .where("appointment_id")
    .equals(fromAppointment)
    .filter((e) => !e.deleted_at)
    .toArray();
  for (const e of list) {
    const recurrence = normLocal(e.recurrence_local);
    if (from && recurrence < from) continue;
    await db.appointment_exceptions.update(e.id, {
      appointment_id: toAppointment,
      recurrence_local: `${recurrence.slice(0, 10)}T${newTime}`,
    });
  }
}

function columns(form: AppointmentForm) {
  const f = fields(form);
  return {
    title: f.title,
    location: f.location,
    notes: f.notes,
    start_local: normLocal(f.startLocal),
    duration_minutes: f.durationMinutes,
    repeat_kind: f.repeat.kind,
    repeat_interval: f.repeat.interval,
    repeat_weekdays: f.repeat.kind === "weekly" ? f.repeat.weekdays : [],
    repeat_until: f.repeat.kind === "none" ? null : f.repeat.until,
    rrule: buildRrule(f.repeat),
    remind_minutes: f.remindMinutes,
    travel_minutes: f.travelMinutes,
    visibility: f.visibility,
  };
}

async function newRow(id: string, form: AppointmentForm): Promise<Appointment> {
  const me = await getMeta<Me>("me");
  const at = new Date().toISOString();
  return {
    id,
    // Together: in the shared agenda (B35); otherwise in the own space.
    space_id: (form.who === "together" ? me?.settings?.shared_space_id : me?.settings?.personal_space_id) ?? "",
    user_id: me?.user.id ?? "",
    version: 0,
    deleted_at: null,
    created_at: at,
    updated_at: at,
    tz: "Europe/Amsterdam",
    ...columns(form),
  };
}

/** New appointment, or the whole series of an existing one. */
export async function saveAppointment(id: string | null, form: AppointmentForm): Promise<string> {
  const appointmentId = id ?? uuidv7();
  // Read outside the transaction: the meta table is not part of it.
  const row = id ? null : await newRow(appointmentId, form);
  const before = id ? await db.appointments.get(id) : undefined;
  const exceptionIds = id ? await db.appointment_exceptions.where("appointment_id").equals(id).primaryKeys() : [];
  const meta = await getMeta<Me>("me");
  const space = meta?.settings
    ? { userId: meta.user.id, shared: meta.settings.shared_space_id ?? "", personal: meta.settings.personal_space_id ?? "" }
    : null;
  await sync.mutate("appointment.save", { id: appointmentId, ...fields(form) }, [appointmentId, ...exceptionIds], async () => {
    if (id) {
      // Only the one who made it moves it between own and together (as on the server).
      const me = space;
      const move = before && me && before.user_id === me.userId ? { space_id: form.who === "together" ? me.shared : me.personal } : {};
      await db.appointments.update(id, { ...columns(form), ...move });
      const newTime = columns(form).start_local.slice(11);
      if (before && normLocal(before.start_local).slice(11) !== newTime) await moveExceptions(id, id, null, newTime);
    } else await db.appointments.add(row!);
  });
  return appointmentId;
}

/** "Deze en volgende": the series ends the day before this occurrence; a new series starts here. */
export async function saveFromOccurrence(appointment: Appointment, occurrence: Occurrence, form: AppointmentForm): Promise<void> {
  if (occurrence.recurrenceLocal <= normLocal(appointment.start_local)) {
    await saveAppointment(appointment.id, form);
    return;
  }
  const newId = uuidv7();
  const until = dayBefore(occurrence.recurrenceLocal);
  const row = await newRow(newId, form);
  await sync.mutate(
    "appointment.saveFrom",
    { id: appointment.id, newId, fromRecurrence: occurrence.recurrenceLocal, ...fields(form) },
    [appointment.id, newId, ...(await db.appointment_exceptions.where("appointment_id").equals(appointment.id).primaryKeys())],
    async () => {
      await db.appointments.update(appointment.id, { repeat_until: until, rrule: buildRrule({ ...repeatOf(appointment), until }) });
      await db.appointments.add(row);
      await moveExceptions(appointment.id, newId, occurrence.recurrenceLocal, row.start_local.slice(11));
    },
  );
}

async function existingException(appointmentId: string, recurrenceLocal: string) {
  return db.appointment_exceptions
    .where("appointment_id")
    .equals(appointmentId)
    .filter((e) => !e.deleted_at && normLocal(e.recurrence_local) === recurrenceLocal)
    .first();
}

/** "Alleen deze keer". */
export async function saveOccurrence(occurrence: Occurrence, form: AppointmentForm): Promise<void> {
  const existing = await existingException(occurrence.appointmentId, occurrence.recurrenceLocal);
  const exceptionId = existing?.id ?? uuidv7();
  const appointment = await db.appointments.get(occurrence.appointmentId);
  const change = {
    cancelled: false,
    new_start_local: normLocal(`${form.date}T${form.time}`),
    new_duration_minutes: form.durationMinutes,
    title: form.title.trim(),
    location: form.location.trim() || null,
  };
  await sync.mutate(
    "appointment.saveOccurrence",
    {
      exceptionId,
      appointmentId: occurrence.appointmentId,
      recurrenceLocal: occurrence.recurrenceLocal,
      startLocal: `${form.date}T${form.time}`,
      durationMinutes: form.durationMinutes,
      title: form.title.trim(),
      location: form.location.trim() || null,
    },
    [exceptionId],
    async () => {
      if (existing) await db.appointment_exceptions.update(existing.id, change);
      else await db.appointment_exceptions.add(exceptionRow(exceptionId, occurrence, appointment?.space_id ?? "", change));
    },
  );
}

export async function skipOccurrence(occurrence: Occurrence): Promise<void> {
  const existing = await existingException(occurrence.appointmentId, occurrence.recurrenceLocal);
  const exceptionId = existing?.id ?? uuidv7();
  const appointment = await db.appointments.get(occurrence.appointmentId);
  await sync.mutate(
    "appointment.skipOccurrence",
    { exceptionId, appointmentId: occurrence.appointmentId, recurrenceLocal: occurrence.recurrenceLocal },
    [exceptionId],
    async () => {
      if (existing) await db.appointment_exceptions.update(existing.id, { cancelled: true });
      else await db.appointment_exceptions.add(exceptionRow(exceptionId, occurrence, appointment?.space_id ?? "", { cancelled: true }));
    },
  );
}

function exceptionRow(
  id: string,
  occurrence: Occurrence,
  spaceId: string,
  change: Partial<{ cancelled: boolean; new_start_local: string; new_duration_minutes: number; title: string; location: string | null }>,
) {
  const at = new Date().toISOString();
  return {
    id,
    space_id: spaceId,
    version: 0,
    deleted_at: null,
    created_at: at,
    updated_at: at,
    appointment_id: occurrence.appointmentId,
    recurrence_local: occurrence.recurrenceLocal,
    cancelled: false,
    new_start_local: null,
    new_duration_minutes: null,
    title: null,
    location: null,
    ...change,
  };
}

export async function removeAppointment(id: string): Promise<void> {
  const at = new Date().toISOString();
  await sync.mutate("appointment.remove", { id, at }, [id], async () => {
    await db.appointments.update(id, { deleted_at: at });
  });
}

/** Notification at the start of a block of a day type on or off; today and later days follow. */
export async function setBlockNotify(templateBlockId: string, notify: boolean, today: string): Promise<void> {
  const days = await db.day_blocks.filter((b) => b.template_block_id === templateBlockId && b.plan_date >= today).toArray();
  await sync.mutate(
    "templateBlock.setNotify",
    { id: templateBlockId, notify, from: addDays(planningDate(new Date()), 1) },
    [templateBlockId, ...days.map((d) => d.id)],
    async () => {
      await db.day_template_blocks.update(templateBlockId, { notify });
      for (const d of days) await db.day_blocks.update(d.id, { notify });
    },
  );
}
